import React, { type FC } from 'react';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faRightLong } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import {
  type Disposition,
  DispositionAbbreviation,
  type Outcome,
} from 'tendril-v2/src/Presentation/Views/types/disposition-mapping.type';
import { type SalesforceMappingsViewmodel } from 'tendril-v2/src/Presentation/Views/Settings/Integrations/Salesforce/Mappings/SalesforceMappings.viewmodel';
import animations from 'tendril-v1/src/core/ui/base/Animations';
import { type SalesforceCallDisposition } from 'tendril-v2/src/Data/DataSource/sync/salesforce-mappings/dto/salesforce-custom-dispositions.dto';

interface SFMappingRowProps {
  viewModel: SalesforceMappingsViewmodel;
  mapping: Disposition;
}
const { tr: Tr } = motion;

const SFMappingRow: FC<SFMappingRowProps> = ({
  mapping,
  viewModel: { selectNewDisposition, customDispositions, viewState },
}) => {
  const { isSuperAdmin, isAdmin, isSalesforceAccount } = viewState;

  const userCanSelect =
    ((isSalesforceAccount && isSuperAdmin) || (isSalesforceAccount && isAdmin)) &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    mapping.abbreviation !== 'default_action';

  const SELECT_ID = String(mapping.customName + mapping.defaultAction + mapping.id).replace(' ', '-');

  const handleSelectItem = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    e.preventDefault();
    const selectedItem = JSON.parse(e.target.value) as SalesforceCallDisposition;

    selectNewDisposition(mapping, selectedItem);
  };

  const selected = customDispositions.find((customDisposition) => {
    if (customDisposition.id === mapping.id) {
      mapping.customName = customDisposition.attributes.name;
      mapping.outcome = customDisposition.attributes.outcome as Outcome;
      return customDisposition;
    }
    return false;
  });

  return (
    <Tr className="table-row" {...animations.fadeInFromTop}>
      <td className="table-cell py-1 px-2">{mapping.defaultAction}</td>
      <td className="table-cell py-1 px-2 text-center">
        <FA icon={faRightLong} size="xl" />
      </td>
      <td className="table-cell py-1  px-2">
        <div className="flex flex-row justify-between gap-2">
          <>
            {userCanSelect ? (
              <>
                <label aria-hidden="true" htmlFor={mapping.abbreviation} className="hidden">
                  Custom Disposition
                </label>
                <select
                  id={SELECT_ID}
                  key={mapping.abbreviation}
                  className="block w-4/5 px-4 py-2 text-base text-gray-400 border border-gray-300 rounded-lg bg-gray-50 ring-none focus:ring-2 focus:ring-app-blue-lighter focus:border-app-blue-light"
                  onChange={handleSelectItem}
                  disabled={mapping.abbreviation === DispositionAbbreviation.default_action || !userCanSelect}
                  data-tip="Select call disposition"
                  data-for={SELECT_ID + '-tooltip'}
                  defaultValue={JSON.stringify(selected)}
                >
                  {customDispositions.map((customDisposition, index) => {
                    const customId = customDisposition.id;
                    const customName = mapping.id !== customId ? customDisposition.attributes.name : mapping.customName;
                    return (
                      <option key={`${index} + ${mapping.abbreviation}`} value={JSON.stringify(customDisposition)}>
                        {customName}
                      </option>
                    );
                  })}
                </select>
              </>
            ) : (
              <h4 key={mapping.id + '-label'} className="text-lg text-bold">
                {mapping.customName}
              </h4>
            )}
          </>
        </div>
      </td>
    </Tr>
  );
};

export default SFMappingRow;
